import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { useReadingDirection } from "@/components/ds/RadioGroup"

/**
 * A row of mutually exclusive options on a pale track: trip type, the sort
 * on a phone, a two-way switch. Semantics are a radio group (role
 * radiogroup, each option role radio with aria-checked): Tab lands on the
 * chosen option, arrow keys move and choose in the reading direction, Home
 * and End jump to the ends. The chosen option is ink with white text, so the
 * state never rests on a shade.
 *
 *   <SegmentedControl aria-label="Trip" value={trip} onValueChange={setTrip}>
 *     <SegmentedControlItem value="round">Round trip</SegmentedControlItem>
 *     <SegmentedControlItem value="oneway">One way</SegmentedControlItem>
 *   </SegmentedControl>
 *
 * Name it with aria-label or aria-labelledby. size md is 44px, lg 56px (in a
 * search panel); fullWidth shares the row equally.
 */
const SegmentedContext = React.createContext({ fullWidth: false })

const trackVariants = cva(
  "items-stretch gap-1 rounded-pill bg-touki-surface p-1",
  {
    variants: {
      size: { md: "h-11", lg: "h-14" },
      fullWidth: { true: "flex w-full", false: "inline-flex w-fit max-w-full" },
    },
    defaultVariants: { size: "md", fullWidth: false },
  }
)

export const SegmentedControl = React.forwardRef(({ className, size, fullWidth = false, dir, children, ...props }, ref) => {
  const direction = useReadingDirection(dir)
  const context = React.useMemo(() => ({ fullWidth }), [fullWidth])
  return (
    <SegmentedContext.Provider value={context}>
      <RadioGroupPrimitive.Root
        ref={ref}
        dir={direction}
        loop
        className={cn(trackVariants({ size, fullWidth }), className)}
        {...props}>
        {children}
      </RadioGroupPrimitive.Root>
    </SegmentedContext.Provider>
  )
})
SegmentedControl.displayName = "SegmentedControl"

export const SegmentedControlItem = React.forwardRef(({ className, children, ...props }, ref) => {
  const { fullWidth } = React.useContext(SegmentedContext)
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "inline-flex min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-pill px-4 text-label-lg font-semibold text-touki-muted-strong",
        "transition-[background-color,color,box-shadow] duration-fast ease-tk-out",
        "hover:text-touki-black data-[state=checked]:bg-touki-dark data-[state=checked]:text-white data-[state=checked]:shadow-tk-card",
        "disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
        fullWidth ? "flex-1" : "flex-none",
        className
      )}
      {...props}>
      {children}
    </RadioGroupPrimitive.Item>
  )
})
SegmentedControlItem.displayName = "SegmentedControlItem"

SegmentedControl.Item = SegmentedControlItem
